import { useCallback, useContext, useEffect } from 'react';

import { SignalREvents } from 'constants/signalR';
import { SignalRContext } from 'contexts/signalR';
import { useSignalREvent } from 'hooks/signalR';
import { useAnsweredAtFallback } from 'hooks/useAnsweredAtFallback';
import { useLiveTranscriptBackfill } from 'hooks/useLiveTranscriptBackfill';
import { fetchAndStoreParticipants, useParticipantsBackfill } from 'hooks/useParticipantsBackfill';
import { noteHubEvent } from 'services/callScribe/hubActivity';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  CallScribeActiveTranscriptCallSelector,
  CallScribeProtectedTranscriptCallSelector,
} from 'store/callScribe/selectors';
import {
  CallScribeCallStatus,
  CallScribePipelineStage,
  CallScribeSentiment,
  CallScribeSpeaker,
} from 'services/callScribe/types';
import {
  applyIntelligenceEvent,
  applyPipelineEvent,
  applyStatusEvent,
  applyTranscriptSegmentEvent,
} from 'store/callScribe';

interface IStatusChangedWire {
  TenantId: number;
  CallId: number;
  Status: CallScribeCallStatus;
  AnsweredAtUtc?: string | null;
  EndedAtUtc?: string | null;
  DurationSeconds?: number | null;
  IsRecording?: boolean | null;
  RecordingStoppedAtUtc?: string | null;
}

interface IIntelligenceReadyWire {
  TenantId: number;
  CallId: number;
  SummaryShort: string | null;
  Sentiment: CallScribeSentiment;
  HasSuggestedTimeEntry: boolean;
}

interface IPipelineProgressWire {
  TenantId: number;
  CallId: number;
  Stage: CallScribePipelineStage;
  ErrorMessage?: string | null;
}

interface ITranscriptSegmentWire {
  TenantId: number;
  CallId: number;
  Seq: number;
  Speaker: CallScribeSpeaker;
  ParticipantId?: number | null;
  Text: string;
  StartMs?: number | null;
  EndMs?: number | null;
  Committed: boolean;
}

interface IParticipantsUpdatedWire {
  TenantId: number;
  CallId: number;
}

export const useSignalRCallScribeEvents = (): void => {
  const dispatch = useAppDispatch();
  const activeCallId = useAppSelector(CallScribeActiveTranscriptCallSelector);
  const protectedCallId = useAppSelector(CallScribeProtectedTranscriptCallSelector);

  const onStatusChanged = useCallback(
    (raw: string) => {
      try {
        const p = JSON.parse(raw) as IStatusChangedWire;
        noteHubEvent(p.CallId);
        dispatch(
          applyStatusEvent({
            callId: p.CallId,
            status: p.Status,
            answeredAtUtc: p.AnsweredAtUtc ?? null,
            endedAtUtc: p.EndedAtUtc ?? null,
            durationSeconds: p.DurationSeconds ?? null,
            isRecording: p.IsRecording ?? null,
            recordingStoppedAtUtc: p.RecordingStoppedAtUtc ?? null,
          }),
        );
      } catch {
        return undefined;
      }
    },
    [dispatch],
  );

  const onIntelligenceReady = useCallback(
    (raw: string) => {
      try {
        const p = JSON.parse(raw) as IIntelligenceReadyWire;
        noteHubEvent(p.CallId);
        dispatch(
          applyIntelligenceEvent({
            callId: p.CallId,
            summaryShort: p.SummaryShort ?? null,
            sentiment: p.Sentiment,
          }),
        );
      } catch {
        return undefined;
      }
    },
    [dispatch],
  );

  const onPipelineProgress = useCallback(
    (raw: string) => {
      try {
        const p = JSON.parse(raw) as IPipelineProgressWire;
        noteHubEvent(p.CallId);
        dispatch(
          applyPipelineEvent({
            callId: p.CallId,
            stage: p.Stage,
            error: p.ErrorMessage ?? null,
          }),
        );
      } catch {
        return undefined;
      }
    },
    [dispatch],
  );

  const onTranscriptSegment = useCallback(
    (raw: string) => {
      try {
        const p = (typeof raw === 'string' ? JSON.parse(raw) : raw) as ITranscriptSegmentWire;
        noteHubEvent(p.CallId);
        // eslint-disable-next-line no-console
        console.debug('[cs] segment event', { callId: p.CallId, seq: p.Seq, speaker: p.Speaker, committed: p.Committed });
        dispatch(
          applyTranscriptSegmentEvent({
            callId: p.CallId,
            segment: {
              seq: p.Seq,
              speaker: p.Speaker,
              participantId: p.ParticipantId ?? null,
              text: p.Text,
              startMs: p.StartMs ?? null,
              endMs: p.EndMs ?? null,
              committed: p.Committed,
            },
          }),
        );
      } catch {
        return undefined;
      }
    },
    [dispatch],
  );

  const onParticipantsUpdated = useCallback(
    (raw: string) => {
      try {
        const p = (typeof raw === 'string' ? JSON.parse(raw) : raw) as IParticipantsUpdatedWire;
        if (typeof p.CallId !== 'number') return undefined;
        noteHubEvent(p.CallId);
        if (p.CallId === activeCallId || p.CallId === protectedCallId) {
          void fetchAndStoreParticipants(dispatch as never, p.CallId);
        }
        return undefined;
      } catch {
        return undefined;
      }
    },
    [dispatch, activeCallId, protectedCallId],
  );

  useSignalREvent(SignalREvents.CallScribeStatusChanged, onStatusChanged);
  useSignalREvent(SignalREvents.CallScribeIntelligenceReady, onIntelligenceReady);
  useSignalREvent(SignalREvents.CallScribePipelineProgress, onPipelineProgress);
  useSignalREvent(SignalREvents.CallScribeTranscriptSegment, onTranscriptSegment);
  useSignalREvent(SignalREvents.CallScribeParticipantsUpdated, onParticipantsUpdated);

  useLiveTranscriptBackfill();
  useParticipantsBackfill();
  useAnsweredAtFallback();

  const { connection, established } = useContext(SignalRContext);
  useEffect(() => {
    if (!connection) return;
    // eslint-disable-next-line no-console
    console.debug('[cs] handlers registered', {
      events: [
        SignalREvents.CallScribeStatusChanged,
        SignalREvents.CallScribeIntelligenceReady,
        SignalREvents.CallScribePipelineProgress,
        SignalREvents.CallScribeTranscriptSegment,
        SignalREvents.CallScribeParticipantsUpdated,
      ],
      connectionState: connection.state,
      established,
    });
  }, [connection, established]);
};
